import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Book, ChevronRight, Pencil, Search } from 'lucide-react';
import { useSubjectList, useCreateSubject, useUpdateSubject } from 'modules/subject';
import { SUBJECT_COLORS } from 'modules/subject/colors';
import type { Subject } from 'modules/subject/types';
import { usePermission } from 'modules/auth/hooks';
import { PERMISSIONS } from 'modules/auth/permissions';
import { toast } from 'components/ui/use-toast';
import { LoadMore } from 'components/ui/pagination';
import { useSearchParamState } from 'hooks/use-search-param-state';
import {
  MobileScreen,
  ScreenHeader,
  HeaderPlus,
  ScreenBody,
  SearchInput,
  ListCard,
  BottomSheet,
  MobileSkeleton,
  MobileEmpty,
  MobileError,
  M,
} from 'components/mobile';

/** Subjects revealed per "load more"; the whole list is already loaded. */
const STEP = 10;

interface SubjectSheetProps {
  open: boolean;
  onClose: () => void;
  title: string;
  name: string;
  onName: (name: string) => void;
  color: string;
  onColor: (color: string) => void;
  submitLabel: string;
  onSubmit: () => void;
  pending: boolean;
}

/** Name and colour for a new or edited subject; saving is the sheet's lime action. */
function SubjectSheet({ open, onClose, title, name, onName, color, onColor, submitLabel, onSubmit, pending }: SubjectSheetProps) {
  const { t } = useTranslation();
  return (
    <BottomSheet open={open} onClose={onClose}>
      <h2 className="mb-4 text-[17px] font-bold" style={{ color: M.ink }}>
        {title}
      </h2>
      <label className="mb-4 block">
        <span className="mb-1.5 block text-[13px] font-medium" style={{ color: M.ink2 }}>
          {t('name')}
        </span>
        <input
          value={name}
          onChange={(e) => onName(e.target.value)}
          placeholder={t('subject_name_placeholder', 'e.g. Mathematics')}
          className="h-11 w-full rounded-md px-3 text-[16px] outline-none"
          style={{ border: `1px solid ${M.border}`, color: M.ink }}
        />
      </label>
      <p className="mb-2 text-[13px] font-medium" style={{ color: M.ink2 }}>
        {t('color')}
      </p>
      <div className="mb-5 flex flex-wrap gap-2.5" role="radiogroup" aria-label={t('color')}>
        {SUBJECT_COLORS.map((c) => (
          <button
            key={c}
            type="button"
            role="radio"
            aria-checked={c === color}
            aria-label={c}
            onClick={() => onColor(c)}
            className="h-[30px] w-[30px] rounded-full"
            style={{ background: c, boxShadow: c === color ? `0 0 0 2px #fff, 0 0 0 4px ${c}` : 'none' }}
          />
        ))}
      </div>
      <button
        type="button"
        onClick={onSubmit}
        disabled={!name.trim() || pending}
        className="flex h-12 w-full items-center justify-center rounded-md text-[15px] font-semibold disabled:opacity-50"
        style={{ background: M.primary, color: M.primaryInk }}
      >
        {submitLabel}
      </button>
    </BottomSheet>
  );
}

/** A searchable card of subjects: tap opens its questions; "+" and the pencil open the subject sheet. */
export function SubjectsMobile() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: subjects = [], isLoading, isError, refetch } = useSubjectList();
  const createSubject = useCreateSubject();
  const updateSubject = useUpdateSubject();
  const canCreate = usePermission(PERMISSIONS.SUBJECTS_CREATE);
  const canEdit = usePermission(PERMISSIONS.SUBJECTS_EDIT);

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Subject | null>(null);
  const [name, setName] = useState('');
  const [color, setColor] = useState(SUBJECT_COLORS[0]);
  const { input: search, setInput: setSearch } = useSearchParamState();
  const [shown, setShown] = useState(STEP);

  const needle = search.trim().toLocaleLowerCase();
  const visible = useMemo(
    () =>
      needle
        ? subjects.filter(
            (s) => s.name.toLocaleLowerCase().includes(needle) || (s.description ?? '').toLocaleLowerCase().includes(needle),
          )
        : subjects,
    [subjects, needle],
  );

  const openCreate = () => {
    setName('');
    setColor(SUBJECT_COLORS[0]);
    setCreateOpen(true);
  };

  const openEdit = (subject: Subject) => {
    setEditTarget(subject);
    setName(subject.name);
    setColor(subject.color || SUBJECT_COLORS[0]);
  };

  const submitCreate = async () => {
    if (!name.trim()) return;
    try {
      await createSubject.mutateAsync({ name: name.trim(), description: '', color });
      setCreateOpen(false);
      setName('');
      setColor(SUBJECT_COLORS[0]);
    } catch (e: any) {
      toast.error(e?.message || t('error'));
    }
  };

  const submitEdit = async () => {
    if (!editTarget || !name.trim()) return;
    try {
      await updateSubject.mutateAsync({
        id: editTarget.id,
        data: { name: name.trim(), color },
      });
      setEditTarget(null);
      setName('');
      setColor(SUBJECT_COLORS[0]);
    } catch (e: any) {
      toast.error(e?.message || t('error'));
    }
  };

  return (
    <MobileScreen>
      <ScreenHeader
        title={t('nav.subjects', 'Subjects')}
        count={isLoading ? undefined : subjects.length}
        right={canCreate ? <HeaderPlus onClick={openCreate} label={t('new_subject', 'New subject')} /> : undefined}
      >
        <div className="pb-3 pt-3">
          <SearchInput
            value={search}
            onChange={(value) => {
              setSearch(value);
              setShown(STEP);
            }}
            placeholder={t('search_subjects')}
          />
        </div>
      </ScreenHeader>

      {isLoading ? (
        <MobileSkeleton />
      ) : isError ? (
        <MobileError onRetry={() => refetch()} />
      ) : subjects.length === 0 ? (
        <MobileEmpty icon={<Book size={22} />} title={t('subjects_empty', 'No subjects yet')} />
      ) : visible.length === 0 ? (
        <MobileEmpty
          icon={<Search size={22} />}
          title={t('no_results')}
          description={t('try_adjusting_your_search_or_filters')}
          action={
            <button
              type="button"
              onClick={() => setSearch('')}
              className="h-10 rounded-md px-4 text-[14px] font-medium"
              style={{ background: M.card, border: `1px solid ${M.border}`, color: M.ink }}
            >
              {t('clear_filters')}
            </button>
          }
        />
      ) : (
        <ScreenBody>
          <ListCard>
            {visible.slice(0, shown).map((subject) => (
              <div
                key={subject.id}
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/app/questions?subjectId=${subject.id}`)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate(`/app/questions?subjectId=${subject.id}`);
                  }
                }}
                className="flex cursor-pointer items-center gap-3 px-3.5 py-[13px]"
              >
                {/* The subject's own colour, as a small key. */}
                <span
                  aria-hidden
                  className="h-2.5 w-2.5 flex-shrink-0 rounded-[3px]"
                  style={{ background: subject.color || M.ink3 }}
                />
                <div className="min-w-0 flex-1">
                  <p className="break-words text-[15px] font-medium leading-snug" style={{ color: M.ink }}>
                    {subject.name}
                  </p>
                  <p className="mt-0.5 text-[12.5px] tabular-nums" style={{ color: M.ink3 }}>
                    {t('n_questions', { count: subject.questionCount, defaultValue: `${subject.questionCount} questions` })}
                  </p>
                </div>
                {canEdit && (
                  <button
                    type="button"
                    aria-label={t('edit_subject_aria', { name: subject.name, defaultValue: 'Edit {{name}}' })}
                    onClick={(e) => {
                      e.stopPropagation();
                      openEdit(subject);
                    }}
                    className="flex h-9 w-9 items-center justify-center rounded-md transition-colors hover:bg-muted"
                    style={{ color: M.ink3 }}
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                )}
                <ChevronRight className="h-4 w-4 flex-shrink-0" style={{ color: M.chevron }} />
              </div>
            ))}
          </ListCard>
          <LoadMore
            compact
            className="mt-4"
            shown={Math.min(shown, visible.length)}
            total={visible.length}
            step={STEP}
            onLoadMore={() => setShown((n) => n + STEP)}
          />
        </ScreenBody>
      )}

      <SubjectSheet
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title={t('new_subject', 'New subject')}
        name={name}
        onName={setName}
        color={color}
        onColor={setColor}
        submitLabel={t('create', 'Create')}
        onSubmit={submitCreate}
        pending={createSubject.isPending}
      />
      <SubjectSheet
        open={Boolean(editTarget)}
        onClose={() => setEditTarget(null)}
        title={t('edit_subject', 'Edit subject')}
        name={name}
        onName={setName}
        color={color}
        onColor={setColor}
        submitLabel={t('save_changes', 'Save changes')}
        onSubmit={submitEdit}
        pending={updateSubject.isPending}
      />
    </MobileScreen>
  );
}

export default SubjectsMobile;
